import { rangesOverlapHalfOpen } from '@/utils/calendarBlockOverlap';

export interface SlotOccupant {
  id: string;
  isBlock: boolean;
  label: string;
  startsAt: Date;
  endsAt: Date;
  patientId: string | null;
  clinicianId: string | null;
}

export type SlotHint =
  | { kind: 'hidden' }
  | { kind: 'free' }
  | { kind: 'patient_other' }
  | { kind: 'practitioner_busy'; label: string }
  | { kind: 'blocked'; label: string };

function sameLocalDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function clockLabel(occupant: SlotOccupant, formatClock: (date: Date) => string): string {
  const name = occupant.label.trim() || 'Aftale';
  return `${name} ${formatClock(occupant.startsAt)}–${formatClock(occupant.endsAt)}`;
}

/**
 * "Ledig tid" is only true when the chosen practitioner has no patient
 * appointment and no block in the slot, and the patient has no other
 * appointment that day. A patient-only check is what showed
 * "Majbritt har ikke andre aftaler" on top of Harald's visit.
 */
export function describeSlotHint(input: {
  patientName: string | null;
  patientId: string | null;
  clinicianId: string | null;
  start: Date | null;
  end: Date | null;
  occupants: readonly SlotOccupant[];
  excludeAppointmentId?: string | null;
  formatClock: (date: Date) => string;
}): SlotHint {
  const { patientName, patientId, clinicianId, start, end, occupants, excludeAppointmentId } =
    input;
  if (!patientName || !start || !end || !(end > start) || !clinicianId) return { kind: 'hidden' };

  const relevant = occupants.filter((row) => row.id !== excludeAppointmentId);
  const practitionerBusy = relevant.filter(
    (row) =>
      !row.isBlock &&
      row.clinicianId === clinicianId &&
      rangesOverlapHalfOpen(start, end, row.startsAt, row.endsAt),
  );
  if (practitionerBusy.length > 0) {
    return {
      kind: 'practitioner_busy',
      label: practitionerBusy.map((row) => clockLabel(row, input.formatClock)).join(', '),
    };
  }

  const blocks = relevant.filter(
    (row) =>
      row.isBlock &&
      row.clinicianId === clinicianId &&
      rangesOverlapHalfOpen(start, end, row.startsAt, row.endsAt),
  );
  if (blocks.length > 0) {
    return {
      kind: 'blocked',
      label: blocks.map((row) => clockLabel(row, input.formatClock)).join(', '),
    };
  }

  const patientOther = relevant.some(
    (row) =>
      !row.isBlock &&
      patientId != null &&
      row.patientId === patientId &&
      sameLocalDay(row.startsAt, start),
  );
  if (patientOther) return { kind: 'patient_other' };
  return { kind: 'free' };
}
